import { DeviceModel, DeviceStatus } from '@shure/shared/angular/data-access/system-api/models';

import { FirmwareUpdateRequest } from './firmware-update-requests';

export enum ScheduledJobType {
	FirmwareUpdate = 'FIRMWARE_UPDATE'
}

export enum ScheduledJobStatus {
	Cancelled = 'CANCELLED',
	Completed = 'COMPLETED',
	Executing = 'EXECUTING',
	Failed = 'FAILED',
	Missed = 'MISSED',
	Pending = 'PENDING'
}

export interface ScheduledJob {
	id: string;
	jobId: string;
	name: string;
	type: ScheduledJobType;
	status: ScheduledJobStatus;
	requestCount: number;
	executeAt: Date;
	createdBy: string;
	createdAt: Date;
	updatedBy: string;
	updatedAt: Date;
}

export interface CreateScheduledJobData {
	deviceIds: string[];
	jobType: ScheduledJobType;
}
export interface ScheduledJobRequestBase {
	id: string;
	type: ScheduledJobType;
	status: ScheduledJobStatus;
	jobId: string;
	executeAt: string;
}

export interface FirmwareUpdateJobRequest extends ScheduledJobRequestBase {
	type: ScheduledJobType.FirmwareUpdate;
	task: {
		packageKey: string;
		packageVersion: string;
		hosted: boolean;
		device: {
			known: boolean;
			nodeId?: string;
			hardwareId: string;
			model: DeviceModel;
			status: DeviceStatus;
			name?: string;
			currentFirmwareVersion?: string;
		};
	};
	result?: FirmwareUpdateRequest;
}
// this will be extended as a union to support other types of requests such as reboot
// e.g.  export type ScheduledJobRequest = FirmwareUpdateRequest | RebootRequest;
export type ScheduledJobRequest = FirmwareUpdateJobRequest;

export interface DeviceScheduledJobFeature {
	firmwareJobRequests: {
		pendingPrimary?: FirmwareUpdateJobRequest;
		pendingHosted?: FirmwareUpdateJobRequest;
		executingPrimary?: FirmwareUpdateJobRequest;
		executingHosted?: FirmwareUpdateJobRequest;
	};
}

type DeviceWithScheduledJobs = { features: { scheduledJobs?: DeviceScheduledJobFeature } };

/**
 * Given an array of 1 or more devices, parses the scheduledJobs.firmwareJobRequests attribute to
 * determine if the pendingPrimary, pendingHosted, executingPrimary, or executingHosted scheduledJob
 * is the same for each device.  If so, that job information is returned in the respective field.
 * @param devices an array of devices, with the features.scheduledJobs attribute.
 * @returns a DeviceScheduledJobFeature object
 */
export function getCommonUpcomingScheduledJobs(devices: DeviceWithScheduledJobs[]): DeviceScheduledJobFeature {
	const allPendingPrimary = getFirmwareRequests(devices, (requests) => requests.pendingPrimary);
	const allPendingHosted = getFirmwareRequests(devices, (requests) => requests.pendingHosted);
	const allExecutingPrimary = getFirmwareRequests(devices, (requests) => requests.executingPrimary);
	const allExecutingHosted = getFirmwareRequests(devices, (requests) => requests.executingHosted);

	return {
		firmwareJobRequests: {
			pendingPrimary: getCommonRequest(allPendingPrimary, devices),
			pendingHosted: getCommonRequest(allPendingHosted, devices),
			executingPrimary: getCommonRequest(allExecutingPrimary, devices),
			executingHosted: getCommonRequest(allExecutingHosted, devices)
		}
	};
}

// local helper functions for getCommonScheduledJobs
const getFirmwareRequests = (
	devices: DeviceWithScheduledJobs[],
	selector: (requests: DeviceScheduledJobFeature['firmwareJobRequests']) => FirmwareUpdateJobRequest | undefined
): FirmwareUpdateJobRequest[] =>
	devices
		.map((device) => selector(device.features.scheduledJobs?.firmwareJobRequests ?? {}))
		.filter((request): request is FirmwareUpdateJobRequest => request !== undefined);

const getCommonRequest = (
	requests: FirmwareUpdateJobRequest[],
	devices: DeviceWithScheduledJobs[]
): FirmwareUpdateJobRequest | undefined => {
	const requestsByJobId = new Map(requests.map((request) => [request.jobId, request]));
	return requests.length === devices.length && requestsByJobId.size === 1 ? requests[0] : undefined;
};
